import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Tracks the bottom edge of `ref`'s element relative to its `offsetParent`,
 * suitable for positioning an absolutely-positioned popup directly underneath it.
 *
 * Re-measures on every render (cheap; React bails out if the value is unchanged)
 * and additionally on element resize / window resize.
 */
export function usePopupTopOffset<T extends HTMLElement = HTMLDivElement>() {
    const ref = useRef<T>(null);
    const [top, setTop] = useState<number>(0);

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const togglerRect = el.getBoundingClientRect();
        const offsetParent = el.offsetParent as HTMLElement | null;
        const next = offsetParent
            ? togglerRect.bottom - offsetParent.getBoundingClientRect().top
            : togglerRect.bottom;
        setTop(prev => (prev === next ? prev : next));
    }, []);

    // Re-measure after every render (no-op if unchanged thanks to setState bailout).
    useLayoutEffect(() => {
        measure();
    });

    // Subscribe to size/window changes once.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [measure]);

    return [ref, top] as const;
}
